import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithTimeout } from '../utils/fetch';
import { API_URL } from '../config';
import PageLoader from '../components/PageLoader';
import { User } from '../types/user';
import { permissionService } from '../services/permissionService';
import { USER_LOGGED_OUT_EVENT, UserLoggedOutDetail } from '../constants/events';

interface AuthState {
  user: User | null;
  permissions: string[]; // Array of permission UUIDs
  userStages: string[]; // Array of stage UUIDs
  routing: RoutingInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface RoutingInfo {
  redirectPath: string;
  data?: any;
  needsSelection: boolean;
}


interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  getUserProjects: (organizationId: string, userId: string) => Promise<any[]>;
}

interface LoginCredentials {
  email: string;
  password: string;
  isclient?: boolean;
}

const INITIAL_STATE: AuthState = {
  user: null,
  permissions: [],
  userStages: [],
  routing: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
};

const AuthContext = createContext<AuthContextType>({
  ...INITIAL_STATE,
  login: async () => {},
  logout: async () => {},
  clearError: () => {},
  getUserProjects: async () => []
});

export const useAuth = () => useContext(AuthContext);

const LOGOUT_STORAGE_KEY = 'checkpoint-global-logout'; // Used to sync logout across tabs

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>(INITIAL_STATE);
  const navigate = useNavigate();
  
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const finalizeLogout = useCallback((detail: UserLoggedOutDetail = { initiatedBy: 'self' }) => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.clear();
      } catch (error) {
        console.warn('Failed to clear localStorage during logout:', error);
      }

      window.dispatchEvent(
        new CustomEvent(USER_LOGGED_OUT_EVENT, { detail })
      );
    }

    setState({ ...INITIAL_STATE, isLoading: false });
    navigate('/login');
  }, [navigate]);

  const refreshAccessToken = useCallback(async () => {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/core/refresh-token`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to refresh token');
      }

      const { accessToken } = await response.json();
      localStorage.setItem('accessToken', accessToken);
      return accessToken;
    } catch (error) {
      throw error;
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      setState(prev => ({ 
        ...prev, 
        isLoading: false, 
        isAuthenticated: false,
        permissions: [],
        userStages: [],
        routing: null
      }));
      return;
    }

    try {
      const isclient = localStorage.getItem('isclient');
      const response = await fetchWithTimeout(
        `${API_URL}/core/validate`,
        {
          method: 'POST',
          headers: { 
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ isclient: isclient }),
          credentials: 'include'
        }
      );
      
      if (response.status === 401) {
        try {
          const newAccessToken = await refreshAccessToken();
          const isclient = localStorage.getItem('isclient');
          const retryResponse = await fetchWithTimeout(
            `${API_URL}/core/validate`,
            {
              method: 'POST',
              headers: { 
                Authorization: `Bearer ${newAccessToken}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ isclient: isclient }),
              credentials: 'include'
            }
          );
          
          if (!retryResponse.ok) {
            throw new Error('Session expired');
          }

          const retryData = await retryResponse.json();
          const { user, projects, codevault, processTemplate, purchaseOrder, assetMaster, hrms, invoice, reports, accounts, projectFinance } = retryData;
          localStorage.setItem('userId', user.id);
          localStorage.setItem('userProjects', JSON.stringify(projects || []));
          localStorage.setItem('codevault', codevault ? 'true' : 'false');
          localStorage.setItem('processTemplate', processTemplate ? 'true' : 'false');
          localStorage.setItem('purchaseorder', purchaseOrder ? 'true' : 'false');
          localStorage.setItem('assetMaster', assetMaster ? 'true' : 'false');
          localStorage.setItem('hrms', hrms ? 'true' : 'false');
          localStorage.setItem('invoice', invoice ? 'true' : 'false');
          localStorage.setItem('reports', reports ? 'true' : 'false');
          localStorage.setItem('accounts', accounts ? 'true' : 'false');
          localStorage.setItem('projectFinance', projectFinance ? 'true' : 'false');
          setState(prev => ({
            ...prev,
            user: retryData.user,
            permissions: retryData.permissions || [],
            userStages: retryData.userStages || [],
            routing: retryData.routing || null,
            isAuthenticated: true,
            error: null,
            isLoading: false
          }));        
          if (retryData.permissions && retryData.permissions.length > 0) {
            await permissionService.initializeCache();
          }
        } catch (refreshError) {
          localStorage.clear();
          setState(prev => ({
            ...prev,
            error: null,
            user: null,
            permissions: [],
            userStages: [],
            routing: null,
            isAuthenticated: false,
            isLoading: false
          }));
        }
      } else if (!response.ok) {
        localStorage.clear();
        setState(prev => ({
          ...prev,
          error: null,
          user: null,
          permissions: [],
          userStages: [],
          routing: null,
          isAuthenticated: false,
          isLoading: false
        }));
      } else {
        const data = await response.json();
        const { user, projects, codevault, processTemplate, purchaseOrder, assetMaster, hrms, invoice, reports, accounts, projectFinance, permissions, stages } = data;
        localStorage.setItem('userId', user.id);
        localStorage.setItem('userProjects', JSON.stringify(projects || []));
        localStorage.setItem('codevault', codevault ? 'true' : 'false');
        localStorage.setItem('processTemplate', processTemplate ? 'true' : 'false');
        localStorage.setItem('purchaseorder', purchaseOrder ? 'true' : 'false');
        localStorage.setItem('assetMaster', assetMaster ? 'true' : 'false');
        localStorage.setItem('hrms', hrms ? 'true' : 'false');
        localStorage.setItem('invoice', invoice ? 'true' : 'false');
        localStorage.setItem('reports', reports ? 'true' : 'false');
        localStorage.setItem('accounts', accounts ? 'true' : 'false');
        localStorage.setItem('projectFinance', projectFinance ? 'true' : 'false');
        
        // Use permissions from response (works for both regular users and client users)
        const userPermissions = permissions || data.permissions || [];
        const userStages = stages || data.userStages || [];
        
        setState(prev => ({
          ...prev,
          user: data.user,
          permissions: userPermissions,
          userStages: userStages,
          routing: data.routing || null,
          isAuthenticated: true,
          error: null,
          isLoading: false
        }));
        if (userPermissions.length > 0) {
          await permissionService.initializeCache();
        }
      }
    } catch (error) {
      localStorage.clear();
      setState(prev => ({
        ...prev,
        error: null,
        user: null,
        isAuthenticated: false,
        isLoading: false
      }));
    }
  }, [refreshAccessToken]);

  const login = useCallback(async (credentials: LoginCredentials): Promise<void> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/core/login`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(credentials),
          credentials: 'include'
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Login failed');
      }

      const { accessToken, user, projects, codevault, processTemplate, purchaseOrder, assetMaster, hrms, invoice, reports, accounts, projectFinance, permissions, stages } = await response.json();
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('userId', user.id);
      localStorage.setItem('selectedOrganizationId', user.organizationId || '');      
      localStorage.setItem('userProjects', JSON.stringify(projects || []));
      localStorage.setItem('codevault', codevault ? 'true' : 'false');
      localStorage.setItem('processTemplate', processTemplate ? 'true' : 'false');
      localStorage.setItem('purchaseorder', purchaseOrder ? 'true' : 'false');
      localStorage.setItem('assetMaster', assetMaster ? 'true' : 'false');
      localStorage.setItem('hrms', hrms ? 'true' : 'false');
      localStorage.setItem('invoice', invoice ? 'true' : 'false');
      localStorage.setItem('reports', reports ? 'true' : 'false');
      localStorage.setItem('accounts', accounts ? 'true' : 'false');
      localStorage.setItem('projectFinance', projectFinance ? 'true' : 'false');
      
      // Store isclient flag for token validation
      if (user.isclient) {
        localStorage.setItem('isclient', 'true');
      } else {
        localStorage.removeItem('isclient');
      }
      
      setState(prev => ({
        ...prev,
        user: { ...user },
        permissions: permissions || [],
        userStages: stages || [],
        isAuthenticated: true,
        error: null,
        isLoading: false
      }));
      
      // Initialize permission service for client users too
      if (permissions && permissions.length > 0) {
        await permissionService.initializeCache();
      }
      
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Login failed',
        user: null,
        permissions: [],
        userStages: [],
        isAuthenticated: false,
        isLoading: false
      }));
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true }));
    
    try {
      await fetchWithTimeout(
        `${API_URL}/core/logout`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(LOGOUT_STORAGE_KEY, Date.now().toString());
        } catch (error) {
          console.warn('Failed to write logout sync key:', error);
        }
      }

      finalizeLogout();
    }
  }, [finalizeLogout]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === LOGOUT_STORAGE_KEY && event.newValue) {
        finalizeLogout({ initiatedBy: 'sync' });
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', handleStorage);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', handleStorage);
      }
    };
  }, [finalizeLogout]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const getUserProjects = useCallback(async (organizationId: string, userId: string): Promise<any[]> => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        return [];
      }
      
      const response = await fetchWithTimeout(
        `${API_URL}/api/organizations/listuserprojects`,
        {
          method: 'POST',
          body: JSON.stringify({ _metadata: { orgContext: organizationId }, userId }),
          headers: { 
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        return [];
      }
      
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Error fetching user projects:', error);
      return [];
    }
  }, []);

  const contextValue = useMemo(() => ({
    ...state,
    login,
    logout,
    clearError,
    getUserProjects
  }), [state, login, logout, clearError, getUserProjects]);

  if (state.isLoading) {
    return <PageLoader />;
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
